import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import {
  DISCONNECT_GOOGLE_BUSINESS_MUTATION,
  GOOGLE_BUSINESS_CONNECTION_QUERY,
  INITIATE_GOOGLE_CONNECT_MUTATION,
  LINK_GOOGLE_LOCATION_MUTATION,
  SET_GOOGLE_BUSINESS_HOLIDAYS_SYNC_ENABLED_MUTATION,
  SET_GOOGLE_BUSINESS_HOURS_SYNC_ENABLED_MUTATION,
  SET_GOOGLE_BUSINESS_SERVICES_SYNC_ENABLED_MUTATION,
  SET_GOOGLE_BUSINESS_SYNC_ENABLED_MUTATION,
  TRIGGER_GBP_HOLIDAYS_PUSH_MUTATION,
  TRIGGER_GBP_HOURS_PUSH_MUTATION,
  TRIGGER_GBP_SERVICES_PUSH_MUTATION,
  TRIGGER_GOOGLE_LOCATION_SYNC_MUTATION,
  TRIGGER_GOOGLE_REVIEWS_SYNC_MUTATION,
} from "@/graphql/google-business";
import { useAsyncAction } from "@/lib/hooks/use-async-action";
import { useSalonStore } from "@/lib/store/salon";

const SYNC_POLL_INTERVAL_MS = 2000;
const SYNC_POLL_TIMEOUT_MS = 30_000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForPushOutcome(
  client: ReturnType<typeof useApolloClient>,
  salonId: string,
  beforePushAt: string | null,
  beforeError: string | null,
  getPushAt: (conn: any) => string | null | undefined,
  getPushError: (conn: any) => string | null | undefined
): Promise<{ error: string | null }> {
  const deadline = Date.now() + SYNC_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await delay(SYNC_POLL_INTERVAL_MS);
    const { data } = await client.query({
      query: GOOGLE_BUSINESS_CONNECTION_QUERY,
      variables: { salonId },
      fetchPolicy: "network-only",
    });
    const conn = data?.googleBusinessConnection;
    if (!conn) continue;
    const currentPushAt = getPushAt(conn) ?? null;
    if (currentPushAt && currentPushAt !== beforePushAt) return { error: null };
    const currentError = getPushError(conn) ?? null;
    if (currentError && currentError !== beforeError) return { error: currentError };
  }
  return { error: null };
}

async function waitForLocationSync(
  client: ReturnType<typeof useApolloClient>,
  salonId: string,
  before: { syncedAt: string | null; error: string | null }
): Promise<{ error: string | null }> {
  const deadline = Date.now() + SYNC_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await delay(SYNC_POLL_INTERVAL_MS);
    const { data } = await client.query({
      query: GOOGLE_BUSINESS_CONNECTION_QUERY,
      variables: { salonId },
      fetchPolicy: "network-only",
    });
    const conn = data?.googleBusinessConnection;
    if (!conn) continue;
    if (conn.lastLocationSyncAt && conn.lastLocationSyncAt !== before.syncedAt) {
      return { error: null };
    }
    if (conn.lastLocationSyncError && conn.lastLocationSyncError !== before.error) {
      return { error: conn.lastLocationSyncError };
    }
  }
  return { error: null };
}

export function useGoogleBusinessConnection() {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;

  const apollo = useApolloClient();

  const { data, loading, error, refetch } = useQuery(GOOGLE_BUSINESS_CONNECTION_QUERY, {
    variables: { salonId: salonId ?? "" },
    skip: !salonId,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const [initiate] = useMutation(INITIATE_GOOGLE_CONNECT_MUTATION);
  const [link] = useMutation(LINK_GOOGLE_LOCATION_MUTATION);
  const [disconnect] = useMutation(DISCONNECT_GOOGLE_BUSINESS_MUTATION);
  const [setSyncEnabled] = useMutation(SET_GOOGLE_BUSINESS_SYNC_ENABLED_MUTATION);
  const [triggerLocationSync] = useMutation(TRIGGER_GOOGLE_LOCATION_SYNC_MUTATION);
  const [setHoursSyncEnabled] = useMutation(SET_GOOGLE_BUSINESS_HOURS_SYNC_ENABLED_MUTATION);
  const [setHolidaysSyncEnabled] = useMutation(SET_GOOGLE_BUSINESS_HOLIDAYS_SYNC_ENABLED_MUTATION);
  const [setServicesSyncEnabled] = useMutation(SET_GOOGLE_BUSINESS_SERVICES_SYNC_ENABLED_MUTATION);
  const [triggerHoursPush] = useMutation(TRIGGER_GBP_HOURS_PUSH_MUTATION);
  const [triggerHolidaysPush] = useMutation(TRIGGER_GBP_HOLIDAYS_PUSH_MUTATION);
  const [triggerServicesPush] = useMutation(TRIGGER_GBP_SERVICES_PUSH_MUTATION);
  const [triggerReviewSync] = useMutation(TRIGGER_GOOGLE_REVIEWS_SYNC_MUTATION);

  const initiateAction = useAsyncAction(
    async () => {
      if (!salonId) throw new Error("No salon selected");
      const result = await initiate({ variables: { salonId } });
      const authUrl = result.data?.initiateGoogleConnect.authUrl;
      if (!authUrl) throw new Error("Failed to start Google connection");
      return authUrl;
    },
    {
      loadingMessage: "Starting Google connection...",
      successMessage: "Redirecting to Google...",
      errorMessage: "Could not start Google connection",
    }
  );

  const linkAction = useAsyncAction(
    async (accountId: string, locationId: string) => {
      if (!salonId) throw new Error("No salon selected");
      const result = await link({
        variables: {
          input: { salonId, accountId, locationId },
        },
        refetchQueries: ["GoogleBusinessConnection"],
        awaitRefetchQueries: true,
      });
      return result.data?.linkGoogleLocation ?? null;
    },
    {
      loadingMessage: "Finishing setup...",
      successMessage: "Google Business connected",
      errorMessage: "Could not link Google location",
    }
  );

  const disconnectAction = useAsyncAction(
    async () => {
      if (!salonId) throw new Error("No salon selected");
      const result = await disconnect({
        variables: { salonId },
        refetchQueries: ["GoogleBusinessConnection"],
        awaitRefetchQueries: true,
      });
      return result.data?.disconnectGoogleBusiness ?? false;
    },
    {
      loadingMessage: "Disconnecting...",
      successMessage: "Google Business disconnected",
      errorMessage: "Could not disconnect Google Business",
    }
  );

  // Toggle actions are intentionally silent on success (no toast) — the
  // toggle's own visual state change is enough feedback, and a toast on every
  // flip would feel noisy. We still surface errors via toast.

  const setSyncEnabledAction = useAsyncAction(
    async (enabled: boolean) => {
      if (!salonId) throw new Error("No salon selected");
      const result = await setSyncEnabled({
        variables: { salonId, enabled },
        refetchQueries: ["GoogleBusinessConnection", "GoogleConnectionHealth"],
        awaitRefetchQueries: true,
      });
      return result.data?.setGoogleBusinessSyncEnabled ?? null;
    },
    {
      errorMessage: "Could not update sync setting",
    }
  );

  const setHoursSyncEnabledAction = useAsyncAction(
    async (enabled: boolean) => {
      if (!salonId) throw new Error("No salon selected");
      await setHoursSyncEnabled({
        variables: { salonId, enabled },
        refetchQueries: ["GoogleBusinessConnection"],
        awaitRefetchQueries: true,
      });
      if (enabled) {
        await triggerHoursPush({ variables: { salonId } });
        const conn = data?.googleBusinessConnection;
        const { error: syncError } = await waitForPushOutcome(
          apollo,
          salonId,
          conn?.lastHoursPushAt ?? null,
          conn?.lastHoursPushError ?? null,
          (c) => c.lastHoursPushAt,
          (c) => c.lastHoursPushError
        );
        if (syncError) throw new Error(syncError);
      }
      return true;
    },
    {
      successMessage: "Hours sync setting updated",
      errorMessage: "Could not update hours sync setting",
    }
  );

  const setHolidaysSyncEnabledAction = useAsyncAction(
    async (enabled: boolean) => {
      if (!salonId) throw new Error("No salon selected");
      await setHolidaysSyncEnabled({
        variables: { salonId, enabled },
        refetchQueries: ["GoogleBusinessConnection"],
        awaitRefetchQueries: true,
      });
      if (enabled) {
        await triggerHolidaysPush({ variables: { salonId } });
        const conn = data?.googleBusinessConnection;
        const { error: syncError } = await waitForPushOutcome(
          apollo,
          salonId,
          conn?.lastHolidaysPushAt ?? null,
          conn?.lastHolidaysPushError ?? null,
          (c) => c.lastHolidaysPushAt,
          (c) => c.lastHolidaysPushError
        );
        if (syncError) throw new Error(syncError);
      }
      return true;
    },
    {
      successMessage: "Holidays sync setting updated",
      errorMessage: "Could not update holidays sync setting",
    }
  );

  const setServicesSyncEnabledAction = useAsyncAction(
    async (enabled: boolean) => {
      if (!salonId) throw new Error("No salon selected");
      await setServicesSyncEnabled({
        variables: { salonId, enabled },
        refetchQueries: ["GoogleBusinessConnection"],
        awaitRefetchQueries: true,
      });
      if (enabled) {
        await triggerServicesPush({ variables: { salonId } });
        const conn = data?.googleBusinessConnection;
        const { error: syncError } = await waitForPushOutcome(
          apollo,
          salonId,
          conn?.lastServicesPushAt ?? null,
          conn?.lastServicesPushError ?? null,
          (c) => c.lastServicesPushAt,
          (c) => c.lastServicesPushError
        );
        if (syncError) throw new Error(syncError);
      }
      return true;
    },
    {
      successMessage: "Services sync setting updated",
      errorMessage: "Could not update services sync setting",
    }
  );

  // Operator-triggered location sync (address + hours only). The mutation only
  // enqueues the background worker, so we keep the action pending while polling
  // for the outcome, then refetch the Business settings queries so the open
  // form reflects the synced address + hours in place (no manual reload).
  const syncLocationAction = useAsyncAction(
    async () => {
      if (!salonId) throw new Error("No salon selected");
      const conn = data?.googleBusinessConnection;
      const before = {
        syncedAt: conn?.lastLocationSyncAt ?? null,
        error: conn?.lastLocationSyncError ?? null,
      };

      const result = await triggerLocationSync({ variables: { salonId } });
      if (!result.data?.triggerGoogleLocationSync) {
        throw new Error("Could not start Google sync");
      }

      const { error: syncError } = await waitForLocationSync(apollo, salonId, before);
      if (syncError) throw new Error(syncError);

      await apollo.refetchQueries({
        include: ["GetSalon", "GetSalonOperatingHours", "GoogleBusinessConnection"],
      });
      return true;
    },
    {
      loadingMessage: "Syncing from Google...",
      successMessage: "Address & hours updated from Google",
      errorMessage: "Could not sync from Google",
    }
  );

  // Manual push triggers — fire-and-forget mutation that enqueues the worker,
  // then poll until the corresponding last*PushAt timestamp advances (success)
  // or last*PushError changes (failure).
  const syncHoursAction = useAsyncAction(
    async () => {
      if (!salonId) throw new Error("No salon selected");
      const conn = data?.googleBusinessConnection;
      const before = {
        pushedAt: conn?.lastHoursPushAt ?? null,
        error: conn?.lastHoursPushError ?? null,
      };

      const result = await triggerHoursPush({ variables: { salonId } });
      if (!result.data?.triggerGbpHoursPush) {
        throw new Error("Could not start hours push");
      }

      const { error: syncError } = await waitForPushOutcome(
        apollo,
        salonId,
        before.pushedAt,
        before.error,
        (c) => c.lastHoursPushAt,
        (c) => c.lastHoursPushError
      );
      if (syncError) throw new Error(syncError);
      return true;
    },
    {
      loadingMessage: "Pushing hours to Google...",
      successMessage: "Hours pushed to Google",
      errorMessage: "Could not push hours to Google",
    }
  );

  const syncHolidaysAction = useAsyncAction(
    async () => {
      if (!salonId) throw new Error("No salon selected");
      const conn = data?.googleBusinessConnection;
      const before = {
        pushedAt: conn?.lastHolidaysPushAt ?? null,
        error: conn?.lastHolidaysPushError ?? null,
      };

      const result = await triggerHolidaysPush({ variables: { salonId } });
      if (!result.data?.triggerGbpHolidaysPush) {
        throw new Error("Could not start holidays push");
      }

      const { error: syncError } = await waitForPushOutcome(
        apollo,
        salonId,
        before.pushedAt,
        before.error,
        (c) => c.lastHolidaysPushAt,
        (c) => c.lastHolidaysPushError
      );
      if (syncError) throw new Error(syncError);
      return true;
    },
    {
      loadingMessage: "Pushing holidays to Google...",
      successMessage: "Holidays pushed to Google",
      errorMessage: "Could not push holidays to Google",
    }
  );

  const syncServicesAction = useAsyncAction(
    async () => {
      if (!salonId) throw new Error("No salon selected");
      const conn = data?.googleBusinessConnection;
      const before = {
        pushedAt: conn?.lastServicesPushAt ?? null,
        error: conn?.lastServicesPushError ?? null,
      };

      const result = await triggerServicesPush({ variables: { salonId } });
      if (!result.data?.triggerGbpServicesPush) {
        throw new Error("Could not start services push");
      }

      const { error: syncError } = await waitForPushOutcome(
        apollo,
        salonId,
        before.pushedAt,
        before.error,
        (c) => c.lastServicesPushAt,
        (c) => c.lastServicesPushError
      );
      if (syncError) throw new Error(syncError);
      return true;
    },
    {
      loadingMessage: "Pushing services to Google...",
      successMessage: "Services pushed to Google",
      errorMessage: "Could not push services to Google",
    }
  );

  const syncReviewsAction = useAsyncAction(
    async () => {
      if (!salonId) throw new Error("No salon selected");
      const conn = data?.googleBusinessConnection;
      const before = {
        syncedAt: conn?.lastSyncAt ?? null,
        error: conn?.lastSyncError ?? null,
      };

      const result = await triggerReviewSync({ variables: { salonId } });
      if (!result.data?.triggerGoogleReviewsSync) {
        throw new Error("Could not start review sync");
      }

      const { error: syncError } = await waitForPushOutcome(
        apollo,
        salonId,
        before.syncedAt,
        before.error,
        (c) => c.lastSyncAt,
        (c) => c.lastSyncError
      );
      if (syncError) throw new Error(syncError);
      return true;
    },
    {
      loadingMessage: "Syncing reviews from Google...",
      successMessage: "Reviews synced from Google",
      errorMessage: "Could not sync reviews from Google",
    }
  );

  return {
    connection: data?.googleBusinessConnection ?? null,
    loading,
    error,
    refetch,
    salonId,
    initiateConnect: initiateAction.execute,
    initiating: initiateAction.isLoading,
    linkLocation: linkAction.execute,
    linking: linkAction.isLoading,
    disconnect: disconnectAction.execute,
    disconnecting: disconnectAction.isLoading,
    setSyncEnabled: setSyncEnabledAction.execute,
    settingSyncEnabled: setSyncEnabledAction.isLoading,
    syncLocationNow: syncLocationAction.execute,
    syncingLocation: syncLocationAction.isLoading,
    setHoursSyncEnabled: setHoursSyncEnabledAction.execute,
    settingHoursSyncEnabled: setHoursSyncEnabledAction.isLoading,
    setHolidaysSyncEnabled: setHolidaysSyncEnabledAction.execute,
    settingHolidaysSyncEnabled: setHolidaysSyncEnabledAction.isLoading,
    setServicesSyncEnabled: setServicesSyncEnabledAction.execute,
    settingServicesSyncEnabled: setServicesSyncEnabledAction.isLoading,
    syncHoursNow: syncHoursAction.execute,
    syncingHours: syncHoursAction.isLoading,
    syncHolidaysNow: syncHolidaysAction.execute,
    syncingHolidays: syncHolidaysAction.isLoading,
    syncServicesNow: syncServicesAction.execute,
    syncingServices: syncServicesAction.isLoading,
    syncReviewsNow: syncReviewsAction.execute,
    syncingReviews: syncReviewsAction.isLoading,
  };
}
